import { useEffect, useMemo, useRef, useState } from 'react';
import { useRouter } from 'expo-router';

import { Button, Text } from '@/components/ui';
import { fetchAccounts } from '@/lib/finance/api';
import { categoryLabelMap, formatMoney, formatUsd } from '@/lib/finance/format';
import { monthLabelShort } from '@/lib/finance/month';
import type { ActualDrillModel, DrillScope } from '@/lib/finance/report';
import type { Account, Category, Transaction } from '@/lib/finance/types';

export function drillKey(scope: DrillScope, month: string | null = null): string {
  const id = scope.kind === 'category' ? String(scope.categoryId) : scope.kind;
  return `${id}:${month ?? 'all'}`;
}

export function DrillNumber({
  text,
  selected,
  label,
  onPress,
}: {
  text: string;
  selected: boolean;
  label: string;
  onPress: () => void;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      aria-pressed={selected}
      className={`w-full cursor-pointer text-right tabular-nums hover:underline ${
        selected ? 'rounded bg-accent-muted px-1' : ''
      }`}
      onClick={onPress}
    >
      {text}
    </button>
  );
}

function categoryName(id: number | null, labels: Map<number, string>): string {
  if (id == null) return 'Uncategorized';
  return labels.get(id) ?? 'Category';
}

function accountName(tx: Transaction, names: Map<number, string>): string {
  const from = tx.accountId != null ? (names.get(tx.accountId) ?? 'Account') : '—';
  if (tx.toAccountId != null && tx.toAccountId !== tx.accountId) {
    const to = names.get(tx.toAccountId) ?? 'Account';
    return `${from} → ${to}`;
  }
  return from;
}

function TxTable({
  transactions,
  categories,
  names,
  totalUsd,
}: {
  transactions: Transaction[];
  categories: Category[];
  names: Map<number, string>;
  totalUsd: number | null;
}) {
  const router = useRouter();
  const labels = useMemo(() => categoryLabelMap(categories), [categories]);
  if (transactions.length === 0) {
    return (
      <Text variant="body-sm" className="text-muted">
        No expense charges in this cell.
      </Text>
    );
  }
  return (
    <div className="max-h-96 overflow-auto">
      <table className="min-w-full border-collapse text-sm">
        <thead>
          <tr className="text-left text-fg-muted">
            <th className="border-b border-border px-2 py-1 font-medium">Date</th>
            <th className="border-b border-border px-2 py-1 font-medium">Account</th>
            <th className="border-b border-border px-2 py-1 font-medium">Category</th>
            <th className="border-b border-border px-2 py-1 font-medium">Notes</th>
            <th className="border-b border-border px-2 py-1 text-right font-medium">Amount</th>
            <th className="border-b border-border px-2 py-1 text-right font-medium">USD</th>
            <th className="border-b border-border px-2 py-1 font-medium" />
          </tr>
        </thead>
        <tbody>
          {transactions.map((tx, index) => (
            <tr key={`${tx.id}:${index}`}>
              <td className="border-b border-border px-2 py-1 whitespace-nowrap">{tx.date}</td>
              <td className="border-b border-border px-2 py-1">{accountName(tx, names)}</td>
              <td className="border-b border-border px-2 py-1">
                {categoryName(tx.categoryId, labels)}
              </td>
              <td className="max-w-md border-b border-border px-2 py-1">{tx.notes ?? ''}</td>
              <td className="border-b border-border px-2 py-1 text-right whitespace-nowrap tabular-nums">
                {formatMoney(tx.amount)} {tx.currencyCode}
              </td>
              <td className="border-b border-border px-2 py-1 text-right tabular-nums">
                {tx.amountUsd == null ? '—' : formatUsd(Math.abs(tx.amountUsd))}
              </td>
              <td className="border-b border-border px-2 py-1">
                <button
                  type="button"
                  className="text-accent hover:underline"
                  onClick={() => router.push(`/finance/transactions/${tx.id}`)}
                >
                  Open
                </button>
              </td>
            </tr>
          ))}
        </tbody>
        {totalUsd != null ? (
          <tfoot>
            <tr className="font-semibold">
              <td className="px-2 py-1" colSpan={5}>
                Total
              </td>
              <td className="px-2 py-1 text-right tabular-nums">{formatUsd(totalUsd)}</td>
              <td />
            </tr>
          </tfoot>
        ) : null}
      </table>
    </div>
  );
}

export function ActualDrilldown({
  title,
  model,
  categories,
  onClose,
}: {
  title: string;
  model: ActualDrillModel;
  categories: Category[];
  onClose: () => void;
}) {
  const rootRef = useRef<HTMLDivElement>(null);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const labels = useMemo(() => categoryLabelMap(categories), [categories]);
  const names = useMemo(
    () => new Map(accounts.map((account) => [account.id, account.name])),
    [accounts],
  );
  const months = new Set(model.budgetLines.map((line) => line.month));

  useEffect(() => {
    rootRef.current?.scrollIntoView({ block: 'nearest' });
  }, [title]);

  useEffect(() => {
    let cancelled = false;
    fetchAccounts()
      .then((rows) => {
        if (!cancelled) setAccounts(rows);
      })
      .catch(() => {
        if (!cancelled) setAccounts([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const included = model.groups.find((group) => group.key === 'included');
  const rest = model.groups.filter((group) => group.key !== 'included');

  return (
    <div ref={rootRef} className="mt-4 rounded-lg border border-border bg-surface-raised p-4">
      <div className="mb-3 flex flex-row items-start justify-between gap-3">
        <Text variant="h3">{title}</Text>
        <Button label="Close" size="sm" variant="secondary" onPress={onClose} />
      </div>
      <div className="mb-3 flex flex-row flex-wrap gap-x-8 gap-y-2">
        {model.summary.map((line) => (
          <div key={line.label}>
            <Text variant="caption" className="text-muted">
              {line.label}
            </Text>
            <Text variant="body" className="font-semibold tabular-nums">
              ${formatUsd(line.usd)}
            </Text>
          </div>
        ))}
      </div>
      {model.note ? (
        <Text variant="caption" className="mb-3 text-muted">
          {model.note}
        </Text>
      ) : null}
      {model.budgetLines.length > 0 ? (
        <div className="mb-4">
          <Text variant="body-sm" className="mb-1 font-medium">
            Budgets added into this cell
          </Text>
          <table className="text-sm">
            <tbody>
              {model.budgetLines.map((line) => (
                <tr key={`${line.month}:${line.categoryId}`}>
                  <td className="py-0.5 pr-6">
                    {months.size > 1 ? `${monthLabelShort(line.month)} · ` : ''}
                    {labels.get(line.categoryId) ?? 'Category'}
                  </td>
                  <td className="py-0.5 text-right tabular-nums">{formatUsd(line.usd)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
      {included ? (
        <TxTable
          transactions={included.transactions}
          categories={categories}
          names={names}
          totalUsd={included.totalUsd}
        />
      ) : null}
      {rest.map((group) => (
        <details key={group.key} className="mt-3">
          <summary className="cursor-pointer text-sm font-medium">
            {group.title}
            {group.totalUsd != null ? ` · $${formatUsd(group.totalUsd)}` : ''} ·{' '}
            {group.transactions.length}
          </summary>
          <div className="mt-2">
            <TxTable
              transactions={group.transactions}
              categories={categories}
              names={names}
              totalUsd={group.totalUsd}
            />
          </div>
        </details>
      ))}
    </div>
  );
}
